import {
  readUiSettingStorageValue,
  useUiSettingStorageListener,
  writeUiSettingStorageValue,
} from '@affine/core/modules/ui-settings-vault';
// Off the workbench barrel: it exports WorkbenchRoot, whose router imports the lazy routes that
// render this surface.
import { useViewMainPane } from '@affine/core/modules/workbench/view/use-view-main-pane';
import {
  ViewBody,
  ViewHeader,
} from '@affine/core/modules/workbench/view/view-islands';
import { CloseIcon } from '@blocksuite/icons/rc';
import clsx from 'clsx';
import {
  type ComponentProps,
  type CSSProperties,
  forwardRef,
  type HTMLAttributes,
  type PointerEventHandler,
  type ReactElement,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  SidebarNavigationFilter,
  SidebarNavigationFilterField,
} from '../../../components/sidebar-navigation-filter';
import { WorkspaceAppSidebarFrame } from '../../../components/workspace-app-sidebar';
import {
  SCROLL_AIR_ATTRIBUTE,
  STREAMLAIN_THREE_PANE_COMPACT_QUERY,
} from '../shared/workspace-pane-layout';
import { WorkspacePaneBottomBarSlot } from './bottom-bar';
import {
  nextWorkspacePaneSidebarOpen,
  WORKSPACE_PANE_SIDEBAR_EVENT,
  type WorkspacePaneSidebarAction,
  type WorkspacePaneSidebarTarget,
} from './sidebar-request';
import * as styles from './workspace-pane.css';

export const WORKSPACE_PANE_DEFAULT_LEADING_PANE_WIDTH = 220;
export const WORKSPACE_PANE_DEFAULT_PRIMARY_PANE_WIDTH = 320;
export const WORKSPACE_PANE_MIN_LEADING_PANE_WIDTH = 180;
export const WORKSPACE_PANE_MAX_LEADING_PANE_WIDTH = 360;
export const WORKSPACE_PANE_MIN_PRIMARY_PANE_WIDTH = 260;
export const WORKSPACE_PANE_MAX_PRIMARY_PANE_WIDTH = 560;
export const WORKSPACE_PANE_PANE_WIDTHS_STORAGE_PREFIX =
  'streamlain.nativeWorkspaceApp.paneWidths.v1';
export const WORKSPACE_PANE_SIDEBAR_VISIBILITY_STORAGE_PREFIX =
  'streamlain.nativeWorkspaceApp.sidebarVisibility.v1';
export const WORKSPACE_PANE_FULL_WIDTH_STORAGE_PREFIX =
  'streamlain.nativeWorkspaceApp.fullWidth.v1';

const matchesWorkspacePaneViewport = (query: string) =>
  typeof window !== 'undefined' && window.matchMedia(query).matches;

export function useWorkspacePaneCompactViewport(): boolean {
  const [matches, setMatches] = useState(() =>
    matchesWorkspacePaneViewport(STREAMLAIN_THREE_PANE_COMPACT_QUERY)
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(STREAMLAIN_THREE_PANE_COMPACT_QUERY);
    const update = () => setMatches(mediaQuery.matches);
    update();
    mediaQuery.addEventListener('change', update);
    return () => mediaQuery.removeEventListener('change', update);
  }, []);

  return matches;
}

/**
 * Only explicit user decisions are remembered; layout-driven changes (e.g. a
 * compact drawer closing after navigation) pass `persist: false`.
 */
export interface WorkspacePaneStoredFlagChange {
  persist?: boolean;
}

export type WorkspacePaneSidebarVisibilityChange =
  WorkspacePaneStoredFlagChange;

/** Local-only boolean UI preference; falls back to `defaultValue` until the user decides. */
export function useWorkspacePaneStoredFlag(
  storageKey: string | null | undefined,
  defaultValue: boolean
): readonly [
  boolean,
  (value: boolean, options?: WorkspacePaneStoredFlagChange) => void,
  { readonly current: boolean },
] {
  const stableStorageKey = useMemo(
    () => normalizedStorageKey(storageKey),
    [storageKey]
  );
  const readStoredValue = useCallback((): boolean | null => {
    if (!stableStorageKey) return null;
    try {
      const raw = readUiSettingStorageValue(
        stableStorageKey,
        WORKSPACE_PANE_PANE_WIDTH_STORAGE_OPTIONS
      );
      return raw === 'true' ? true : raw === 'false' ? false : null;
    } catch {
      return null;
    }
  }, [stableStorageKey]);
  const [value, setValueState] = useState(
    () => readStoredValue() ?? defaultValue
  );
  const valueRef = useRef(value);

  useEffect(() => {
    const nextValue = readStoredValue() ?? defaultValue;
    valueRef.current = nextValue;
    setValueState(nextValue);
  }, [defaultValue, readStoredValue]);

  const setValue = useCallback(
    (next: boolean, options?: WorkspacePaneStoredFlagChange) => {
      valueRef.current = next;
      setValueState(next);
      if (!stableStorageKey || options?.persist === false) return;
      try {
        writeUiSettingStorageValue(
          stableStorageKey,
          String(next),
          WORKSPACE_PANE_PANE_WIDTH_STORAGE_OPTIONS
        );
      } catch {
        // UI preferences should never break app rendering.
      }
    },
    [stableStorageKey]
  );

  return [value, setValue, valueRef] as const;
}

export function useWorkspacePaneSidebarVisibility(
  target: WorkspacePaneSidebarTarget,
  {
    defaultOpen = true,
    storageKey,
  }: {
    defaultOpen?: boolean;
    storageKey?: string | null;
  } = {}
): readonly [
  boolean,
  (open: boolean, options?: WorkspacePaneSidebarVisibilityChange) => void,
] {
  const [open, setOpen, openRef] = useWorkspacePaneStoredFlag(
    storageKey,
    defaultOpen
  );

  useEffect(() => {
    const handleRequest = (event: Event) => {
      const detail = (
        event as CustomEvent<{
          action?: WorkspacePaneSidebarAction;
          target?: WorkspacePaneSidebarTarget;
        }>
      ).detail;
      if (detail?.target !== target) return;
      setOpen(nextWorkspacePaneSidebarOpen(detail.action, openRef.current));
    };

    window.addEventListener(WORKSPACE_PANE_SIDEBAR_EVENT, handleRequest);
    return () =>
      window.removeEventListener(WORKSPACE_PANE_SIDEBAR_EVENT, handleRequest);
  }, [openRef, setOpen, target]);

  return [open, setOpen] as const;
}

const WORKSPACE_PANE_PANE_WIDTH_STORAGE_OPTIONS = {
  sync: 'local-only',
} as const;

export interface WorkspacePanePaneWidths {
  leading: number;
  primary: number;
}

type WorkspacePanePaneWidthsUpdater =
  | WorkspacePanePaneWidths
  | ((current: WorkspacePanePaneWidths) => WorkspacePanePaneWidths);

const DEFAULT_WORKSPACE_PANE_PANE_WIDTHS: WorkspacePanePaneWidths = {
  leading: WORKSPACE_PANE_DEFAULT_LEADING_PANE_WIDTH,
  primary: WORKSPACE_PANE_DEFAULT_PRIMARY_PANE_WIDTH,
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function clampPaneWidth(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function normalizePaneWidth(
  value: unknown,
  fallback: number,
  min: number,
  max: number
): number {
  return typeof value === 'number' && Number.isFinite(value)
    ? clampPaneWidth(Math.round(value), min, max)
    : fallback;
}

function normalizeWorkspacePanePaneWidths(
  value: unknown
): WorkspacePanePaneWidths {
  if (!isRecord(value)) return DEFAULT_WORKSPACE_PANE_PANE_WIDTHS;

  return {
    leading: normalizePaneWidth(
      value['leading'],
      WORKSPACE_PANE_DEFAULT_LEADING_PANE_WIDTH,
      WORKSPACE_PANE_MIN_LEADING_PANE_WIDTH,
      WORKSPACE_PANE_MAX_LEADING_PANE_WIDTH
    ),
    primary: normalizePaneWidth(
      value['primary'],
      WORKSPACE_PANE_DEFAULT_PRIMARY_PANE_WIDTH,
      WORKSPACE_PANE_MIN_PRIMARY_PANE_WIDTH,
      WORKSPACE_PANE_MAX_PRIMARY_PANE_WIDTH
    ),
  };
}

function readWorkspacePanePaneWidths(
  storageKey: string | null
): WorkspacePanePaneWidths {
  if (!storageKey) return DEFAULT_WORKSPACE_PANE_PANE_WIDTHS;

  try {
    const raw = readUiSettingStorageValue(
      storageKey,
      WORKSPACE_PANE_PANE_WIDTH_STORAGE_OPTIONS
    );
    if (!raw) return DEFAULT_WORKSPACE_PANE_PANE_WIDTHS;
    return normalizeWorkspacePanePaneWidths(JSON.parse(raw) as unknown);
  } catch {
    return DEFAULT_WORKSPACE_PANE_PANE_WIDTHS;
  }
}

function writeWorkspacePanePaneWidths(
  storageKey: string | null,
  paneWidths: WorkspacePanePaneWidths
): void {
  if (!storageKey) return;

  try {
    writeUiSettingStorageValue(
      storageKey,
      JSON.stringify(normalizeWorkspacePanePaneWidths(paneWidths)),
      WORKSPACE_PANE_PANE_WIDTH_STORAGE_OPTIONS
    );
  } catch {
    // Pane widths should never break app rendering in private/quota contexts.
  }
}

function encodeStorageScopePart(value: string): string {
  return encodeURIComponent(value.trim());
}

function normalizedStorageKey(
  storageKey: string | null | undefined
): string | null {
  const normalized = storageKey?.trim();
  return normalized ? normalized : null;
}

interface WorkspacePaneStorageScope {
  appId: string | null | undefined;
  workspaceId: string | null | undefined;
}

function createWorkspacePaneScopedStorageKey(
  prefix: string,
  { appId, workspaceId }: WorkspacePaneStorageScope
): string | null {
  const normalizedAppId = appId?.trim();
  const normalizedWorkspaceId = workspaceId?.trim();
  if (!normalizedAppId || !normalizedWorkspaceId) return null;

  return `${prefix}:app:${encodeStorageScopePart(
    normalizedAppId
  )}:workspace:${encodeStorageScopePart(normalizedWorkspaceId)}`;
}

export function createWorkspacePanePaneWidthsStorageKey(
  scope: WorkspacePaneStorageScope
): string | null {
  return createWorkspacePaneScopedStorageKey(
    WORKSPACE_PANE_PANE_WIDTHS_STORAGE_PREFIX,
    scope
  );
}

export function createWorkspacePaneSidebarVisibilityStorageKey(
  scope: WorkspacePaneStorageScope
): string | null {
  return createWorkspacePaneScopedStorageKey(
    WORKSPACE_PANE_SIDEBAR_VISIBILITY_STORAGE_PREFIX,
    scope
  );
}

export function createWorkspacePaneFullWidthStorageKey(
  scope: WorkspacePaneStorageScope
): string | null {
  return createWorkspacePaneScopedStorageKey(
    WORKSPACE_PANE_FULL_WIDTH_STORAGE_PREFIX,
    scope
  );
}

export function createWorkspacePanePaneWidthsStyle(
  paneWidths: WorkspacePanePaneWidths
): CSSProperties {
  const normalized = normalizeWorkspacePanePaneWidths(paneWidths);
  return {
    '--native-app-leading-pane-width': `${normalized.leading}px`,
    '--native-app-sidebar-overlay-width': '320px',
    '--native-app-sidebar-width': `${normalized.leading}px`,
    '--native-app-primary-pane-width': `${normalized.primary}px`,
  } as CSSProperties;
}

export function useWorkspacePanePaneWidths(
  storageKey: string | null | undefined
) {
  const stableStorageKey = useMemo(
    () => normalizedStorageKey(storageKey),
    [storageKey]
  );
  const [paneWidths, setPaneWidthsState] = useState<WorkspacePanePaneWidths>(
    () => readWorkspacePanePaneWidths(stableStorageKey)
  );

  useEffect(() => {
    setPaneWidthsState(readWorkspacePanePaneWidths(stableStorageKey));
  }, [stableStorageKey]);

  useUiSettingStorageListener(
    stableStorageKey,
    WORKSPACE_PANE_PANE_WIDTH_STORAGE_OPTIONS,
    useCallback(rawValue => {
      try {
        setPaneWidthsState(
          normalizeWorkspacePanePaneWidths(JSON.parse(rawValue) as unknown)
        );
      } catch {
        setPaneWidthsState(DEFAULT_WORKSPACE_PANE_PANE_WIDTHS);
      }
    }, [])
  );

  const setPaneWidths = useCallback(
    (next: WorkspacePanePaneWidthsUpdater) => {
      setPaneWidthsState(current => {
        const value = (() => {
          if (typeof next !== 'function') return next;
          const update = next as (
            current: WorkspacePanePaneWidths
          ) => WorkspacePanePaneWidths;
          return update(current);
        })();
        const normalized = normalizeWorkspacePanePaneWidths(value);
        writeWorkspacePanePaneWidths(stableStorageKey, normalized);
        return normalized;
      });
    },
    [stableStorageKey]
  );

  return [paneWidths, setPaneWidths] as const;
}

export function useWorkspacePanePrimaryPaneController(
  storageKey: string | null | undefined,
  {
    maxWidth = WORKSPACE_PANE_MAX_PRIMARY_PANE_WIDTH,
    minWidth = WORKSPACE_PANE_MIN_PRIMARY_PANE_WIDTH,
  }: {
    maxWidth?: number;
    minWidth?: number;
  } = {}
) {
  const [paneWidths, setPaneWidths] = useWorkspacePanePaneWidths(storageKey);
  const setPrimaryPaneWidth = useCallback(
    (width: number) => {
      setPaneWidths(current => ({
        ...current,
        primary: clampPaneWidth(width, minWidth, maxWidth),
      }));
    },
    [maxWidth, minWidth, setPaneWidths]
  );
  const startPrimaryPaneResize = useCallback<
    PointerEventHandler<HTMLDivElement>
  >(
    event => {
      event.preventDefault();
      const startX = event.clientX;
      const startWidth = paneWidths.primary;
      const onPointerMove = (moveEvent: PointerEvent) => {
        const nextWidth = clampPaneWidth(
          startWidth + moveEvent.clientX - startX,
          minWidth,
          maxWidth
        );
        setPaneWidths(current => ({
          ...current,
          primary: nextWidth,
        }));
      };
      const onPointerUp = () => {
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUp);
      };
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp, { once: true });
    },
    [maxWidth, minWidth, paneWidths.primary, setPaneWidths]
  );

  return {
    primaryPaneWidth: paneWidths.primary,
    setPrimaryPaneWidth,
    startPrimaryPaneResize,
  } as const;
}

export interface WorkspacePaneAction {
  disabled?: boolean;
  hint?: ReactNode;
  icon?: ReactNode;
  id: string;
  onClick?: () => void;
  title: ReactNode;
  variant?: 'primary' | 'secondary';
}

export function WorkspacePaneActionList({
  actions,
  compact = false,
  standalone = false,
}: {
  actions: WorkspacePaneAction[];
  compact?: boolean;
  standalone?: boolean;
}): ReactElement {
  return (
    <div
      className={clsx(
        styles.welcomeActions,
        compact && styles.welcomeActionsCompact,
        standalone && styles.welcomeActionsStandalone
      )}
    >
      {actions.map(action => (
        <button
          key={action.id}
          className={clsx(
            styles.welcomeAction,
            compact && styles.welcomeActionCompact,
            action.variant === 'primary' && styles.welcomeActionPrimary
          )}
          type="button"
          disabled={action.disabled}
          onClick={action.onClick}
        >
          {action.icon ? (
            <span
              className={clsx(
                styles.welcomeActionIcon,
                action.variant === 'secondary' &&
                  styles.welcomeActionIconSecondary
              )}
            >
              {action.icon}
            </span>
          ) : null}
          <span className={styles.welcomeActionText}>
            <span className={styles.welcomeActionTitle}>{action.title}</span>
            {action.hint ? (
              <span
                className={clsx(
                  styles.welcomeActionHint,
                  compact && styles.welcomeActionHintCompact
                )}
              >
                {action.hint}
              </span>
            ) : null}
          </span>
        </button>
      ))}
    </div>
  );
}

export function WorkspacePaneHeader({
  children,
  collapsed = false,
  icon,
  leading,
  title,
}: {
  children?: ReactNode;
  collapsed?: boolean;
  icon?: ReactNode;
  leading?: ReactNode;
  title: ReactNode;
}): ReactElement {
  return (
    <ViewHeader>
      <div
        className={styles.headerBar}
        data-workspace-pane-header-collapsed={collapsed ? 'true' : undefined}
      >
        {leading}
        {icon ? <span className={styles.headerIcon}>{icon}</span> : null}
        <div className={styles.headerTitle}>{title}</div>
        {children}
      </div>
    </ViewHeader>
  );
}

export function WorkspacePaneHeaderSpacer(): ReactElement {
  return <div className={styles.headerSpacer} />;
}

export function WorkspacePaneHeaderToolbar({
  children,
}: {
  children?: ReactNode;
}): ReactElement {
  return <div className={styles.headerToolbar}>{children}</div>;
}

export function WorkspacePaneBody({
  children,
  testId,
}: {
  children?: ReactNode;
  testId?: string;
}): ReactElement {
  return (
    <ViewBody>
      <div className={styles.bodyFill} data-testid={testId}>
        {children}
      </div>
    </ViewBody>
  );
}

export function WorkspacePaneShell({
  children,
}: {
  children?: ReactNode;
}): ReactElement {
  return <div className={styles.appShell}>{children}</div>;
}

export function WorkspacePaneSurface({
  children,
  className,
  testId,
  objectRef,
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  children?: ReactNode;
  testId?: string;
  objectRef?: ComponentProps<typeof ViewBody>['objectRef'];
}): ReactElement {
  return (
    <ViewBody objectRef={objectRef}>
      <div
        className={clsx(styles.surface, className)}
        data-testid={testId}
        {...props}
      >
        <WorkspacePaneBottomBarSlot>{children}</WorkspacePaneBottomBarSlot>
      </div>
    </ViewBody>
  );
}

export function WorkspacePaneContentFill({
  children,
  className,
  testId,
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  children?: ReactNode;
  testId?: string;
}): ReactElement {
  return (
    <div
      className={clsx(styles.contentFill, className)}
      data-testid={testId}
      {...props}
    >
      {children}
    </div>
  );
}

export function WorkspacePaneThreePaneLayout({
  children,
  className,
  compactDetailOpen = false,
  focused = false,
  sidebarOpen = true,
  sidebarResizing = false,
  style,
  testId,
}: {
  children?: ReactNode;
  className?: string;
  compactDetailOpen?: boolean;
  focused?: boolean;
  sidebarOpen?: boolean;
  sidebarResizing?: boolean;
  style?: CSSProperties;
  testId?: string;
}): ReactElement {
  return (
    <main
      className={clsx(
        styles.threePaneLayout,
        focused && styles.threePaneLayoutFocused,
        className
      )}
      data-sidebar-open={sidebarOpen}
      data-sidebar-resizing={sidebarResizing}
      data-compact-detail-open={compactDetailOpen}
      data-focused={focused}
      data-testid={testId}
      style={style}
    >
      {children}
    </main>
  );
}

function WorkspacePaneThreePaneSlot({
  children,
  className,
  inactive = false,
  paneRef,
}: {
  children?: ReactNode;
  className: string;
  inactive?: boolean;
  paneRef?: RefObject<HTMLDivElement | null>;
}): ReactElement {
  return (
    <div
      ref={paneRef}
      aria-hidden={inactive ? true : undefined}
      className={className}
      inert={inactive ? true : undefined}
    >
      {children}
    </div>
  );
}

export function WorkspacePaneThreePanePrimary({
  children,
  inactive = false,
}: {
  children?: ReactNode;
  inactive?: boolean;
}): ReactElement {
  return (
    <WorkspacePaneThreePaneSlot
      className={styles.threePanePrimaryPane}
      inactive={inactive}
    >
      {children}
    </WorkspacePaneThreePaneSlot>
  );
}

export function WorkspacePaneThreePaneDetail({
  children,
  inactive = false,
}: {
  children?: ReactNode;
  inactive?: boolean;
}): ReactElement {
  // The detail pane right of the list is the view's main pane.
  const pane = useRef<HTMLDivElement>(null);
  useViewMainPane(pane);
  return (
    <WorkspacePaneThreePaneSlot
      className={styles.threePaneDetailPane}
      inactive={inactive}
      paneRef={pane}
    >
      {children}
    </WorkspacePaneThreePaneSlot>
  );
}

export function WorkspacePanePane({
  as = 'section',
  children,
  className,
  variant = 'primary',
}: {
  as?: 'aside' | 'div' | 'section';
  children?: ReactNode;
  className?: string;
  variant?: 'primary' | 'secondary';
}): ReactElement {
  const paneClassName = clsx(
    styles.pane,
    variant === 'secondary' && styles.paneSecondary,
    className
  );

  if (as === 'aside') {
    return <aside className={paneClassName}>{children}</aside>;
  }
  if (as === 'div') {
    return <div className={paneClassName}>{children}</div>;
  }
  return <section className={paneClassName}>{children}</section>;
}

export function WorkspacePanePaneHeader({
  action,
  children,
  closeLabel = 'Close',
  onClose,
}: {
  action?: ReactNode;
  children?: ReactNode;
  closeLabel?: string;
  onClose?: () => void;
}): ReactElement {
  return (
    <div className={styles.paneHeader}>
      {children ? <span className={styles.paneTitle}>{children}</span> : null}
      {action || onClose ? (
        <span className={styles.paneHeaderActions}>
          {action}
          {onClose ? (
            <button
              aria-label={closeLabel}
              className={styles.paneCloseButton}
              onClick={onClose}
              type="button"
            >
              <CloseIcon />
            </button>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}

export function WorkspacePaneSidebarStatusOverlay({
  children,
}: {
  children?: ReactNode;
}): ReactElement | null {
  if (!children) return null;

  return <div className={styles.sidebarStatusOverlay}>{children}</div>;
}

export const WorkspacePaneScrollPane = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & {
    children?: ReactNode;
  }
>(function WorkspacePaneScrollPane(
  { children, className, ...props },
  ref
): ReactElement {
  return (
    <div
      ref={ref}
      className={clsx(styles.scrollPane, className)}
      {...{ [SCROLL_AIR_ATTRIBUTE]: '' }}
      {...props}
    >
      {children}
    </div>
  );
});

export function WorkspacePanePaneResizeHandle({
  className,
  hiddenOnCompact = false,
  hiddenOnMobile = false,
  label,
  onPointerDown,
}: {
  className?: string;
  hiddenOnCompact?: boolean;
  hiddenOnMobile?: boolean;
  label: string;
  onPointerDown: PointerEventHandler<HTMLDivElement>;
}): ReactElement {
  return (
    <div
      className={clsx(
        styles.paneResizeHandle,
        hiddenOnCompact && styles.paneResizeHandleHiddenOnCompact,
        hiddenOnMobile && styles.paneResizeHandleHiddenOnMobile,
        className
      )}
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      onPointerDown={onPointerDown}
    />
  );
}

export function WorkspacePaneSidebar({
  action,
  children,
  closeLabel,
  contentClassName,
  contentPadding = 'default',
  contentProps,
  footer,
  header,
  onClose,
  status,
  title,
  searchPlaceholder,
}: {
  action?: ReactNode;
  children?: ReactNode;
  closeLabel?: string;
  contentClassName?: string;
  contentPadding?: 'default' | 'none';
  contentProps?: Omit<ComponentProps<'div'>, 'children' | 'className'>;
  /** Pinned below the scrolling content. */
  footer?: ReactNode;
  header?: ReactNode;
  onClose?: () => void;
  status?: ReactNode;
  title?: ReactNode;
  searchPlaceholder?: string;
}): ReactElement {
  const frame = (
    <WorkspaceAppSidebarFrame
      className={clsx(
        styles.sidebar,
        searchPlaceholder && styles.searchableSidebar
      )}
      contentClassName={clsx(
        styles.scrollPane,
        contentPadding === 'none' && styles.scrollPaneNoPadding,
        contentClassName
      )}
      contentProps={{ 'data-sidebar-scroll-viewport': true, ...contentProps }}
      footer={footer}
      header={
        searchPlaceholder ? (
          <SidebarNavigationFilterField />
        ) : (
          (header ?? (
            <WorkspacePanePaneHeader
              action={action}
              closeLabel={closeLabel}
              onClose={onClose}
            >
              {title}
            </WorkspacePanePaneHeader>
          ))
        )
      }
      status={
        <WorkspacePaneSidebarStatusOverlay>
          {status}
        </WorkspacePaneSidebarStatusOverlay>
      }
    >
      {children}
    </WorkspaceAppSidebarFrame>
  );
  return searchPlaceholder ? (
    <SidebarNavigationFilter placeholder={searchPlaceholder}>
      {frame}
    </SidebarNavigationFilter>
  ) : (
    frame
  );
}

export function WorkspacePaneSidebarItem({
  active,
  badge,
  color,
  icon,
  meta,
  onClick,
  title,
}: {
  active?: boolean;
  badge?: ReactNode;
  color?: string | null;
  icon?: ReactNode;
  meta?: ReactNode;
  onClick?: () => void;
  title: ReactNode;
}): ReactElement {
  return (
    <button
      className={active ? styles.sidebarItemActive : styles.sidebarItem}
      type="button"
      onClick={onClick}
    >
      <span className={styles.sidebarItemIcon}>
        {color ? (
          <span
            className={styles.sidebarItemIconColor}
            style={{ background: color }}
          />
        ) : (
          icon
        )}
      </span>
      <span className={styles.sidebarItemText}>
        <span className={styles.sidebarItemTitle}>{title}</span>
        {meta ? <span className={styles.sidebarItemMeta}>{meta}</span> : null}
      </span>
      {badge ? <span className={styles.badge}>{badge}</span> : null}
    </button>
  );
}

export function WorkspacePaneEmptyState({
  actions,
  hint,
  icon,
  testId,
  title,
}: {
  actions?: ReactNode;
  hint?: ReactNode;
  icon?: ReactNode;
  testId?: string;
  title: ReactNode;
}): ReactElement {
  return (
    <div className={styles.emptyState} data-testid={testId}>
      {icon ? <span className={styles.emptyIcon}>{icon}</span> : null}
      <h2 className={styles.emptyTitle}>{title}</h2>
      {hint ? <p className={styles.emptyHint}>{hint}</p> : null}
      {actions}
    </div>
  );
}

export function WorkspacePaneWelcome({
  actions,
  children,
  footnote,
  icon,
  subtitle,
  surface = 'primary',
  testId,
  title,
  variant = 'default',
}: {
  actions?: WorkspacePaneAction[];
  children?: ReactNode;
  footnote?: ReactNode;
  icon?: ReactNode;
  subtitle?: ReactNode;
  surface?: 'appChrome' | 'primary';
  testId?: string;
  title: ReactNode;
  variant?: 'compact' | 'default';
}): ReactElement {
  const compact = variant === 'compact';

  return (
    <div
      className={clsx(styles.welcome, compact && styles.welcomeCompact)}
      data-testid={testId}
    >
      <section
        className={clsx(
          styles.welcomeCard,
          surface === 'appChrome' && styles.welcomeCardAppChrome,
          compact && styles.welcomeCardCompact
        )}
      >
        <header
          className={clsx(
            styles.welcomeHeader,
            icon && styles.welcomeHeaderWithIcon,
            compact && styles.welcomeHeaderCompact
          )}
        >
          {icon ? (
            <span
              className={clsx(
                styles.welcomeIcon,
                compact && styles.welcomeIconCompact
              )}
            >
              {icon}
            </span>
          ) : null}
          <div className={styles.welcomeHeaderText}>
            <h1
              className={clsx(
                styles.welcomeTitle,
                compact && styles.welcomeTitleCompact
              )}
            >
              {title}
            </h1>
            {subtitle ? (
              <p
                className={clsx(
                  styles.welcomeSubtitle,
                  compact && styles.welcomeSubtitleCompact
                )}
              >
                {subtitle}
              </p>
            ) : null}
          </div>
        </header>
        {actions?.length ? (
          <WorkspacePaneActionList actions={actions} compact={compact} />
        ) : null}
        {children ? (
          <div className={styles.welcomeContent}>{children}</div>
        ) : null}
        {footnote ? <p className={styles.welcomeFootnote}>{footnote}</p> : null}
      </section>
    </div>
  );
}

export const workspacePaneClasses = styles;
